import { clientObjectSchema, type ClientObjectDto } from '@kominiarz/shared';
import { useState } from 'react';
import { AddressField, type Coords } from '../../components/AddressField';
import { Alert } from '../../components/Alert';
import { OfflineNotice } from '../../components/OfflineNotice';
import { useOnline } from '../../lib/appStatus';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { TextArea } from '../../components/form/TextArea';
import { TextInput } from '../../components/form/TextInput';
import { useToast } from '../../components/Toast';
import { errorMessage } from '../../lib/api';
import { useAddObject, useUpdateObject } from '../../lib/clientsApi';

/** Adds or edits one of the client's addresses (property). */
export function ObjectDialog({ clientId, object, onClose }: { clientId: number; object: ClientObjectDto | null; onClose: () => void }) {
  const toast = useToast();
  const add = useAddObject(clientId);
  const update = useUpdateObject();
  const online = useOnline();
  const [label, setLabel] = useState(object?.label ?? '');
  const [address, setAddress] = useState(object?.address ?? '');
  const [coords, setCoords] = useState<Coords | null>(
    object?.lat != null && object?.lng != null ? { lat: object.lat, lng: object.lng } : null,
  );
  const [note, setNote] = useState(object?.note ?? '');
  const [due, setDue] = useState(object?.nextInspectionDue ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  async function save() {
    const parsed = clientObjectSchema.safeParse({
      label,
      address,
      note,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      nextInspectionDue: due || null,
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    try {
      if (object) {
        await update.mutateAsync({ id: object.id, body: parsed.data });
      } else {
        await add.mutateAsync(parsed.data);
      }
      toast.success('Zapisano adres');
      onClose();
    } catch (err) {
      setFailure(errorMessage(err));
    }
  }

  return (
    <Dialog
      open
      title={object ? 'Adres klienta' : 'Nowy adres klienta'}
      onClose={onClose}
      actions={
        <>
          <Button variant="primary" size="lg" onClick={save} disabled={add.isPending || update.isPending || !online}>
            Zapisz
          </Button>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Anuluj
          </Button>
        </>
      }
    >
      <OfflineNotice />
      <TextInput
        label="Nazwa"
        optional
        placeholder="np. Dom, Garaż, Kamienica"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        error={errors.label}
      />
      <AddressField
        label="Adres"
        value={address}
        coords={coords}
        onType={(a) => {
          setAddress(a);
          setCoords(null);
        }}
        onPick={(a, c) => {
          setAddress(a);
          setCoords(c);
        }}
        onPin={setCoords}
        error={errors.address}
      />
      <TextArea
        label="Uwagi"
        optional
        rows={3}
        placeholder="np. kod do bramy, pies, piec gazowy w piwnicy"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        error={errors.note}
      />
      <TextInput
        type="date"
        label="Termin kolejnego przeglądu"
        optional
        hint="Adres pojawi się w „Do umówienia” przed tym terminem. Zwykle ustawia się go przy zamykaniu wizyty."
        value={due}
        onChange={(e) => setDue(e.target.value)}
        error={errors.nextInspectionDue}
      />
      {failure && <Alert kind="error">{failure}</Alert>}
    </Dialog>
  );
}
